import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { GuideViewer, MocFlow } from './AideGuides';
import {
  SquaresFour, PlusCircle, Folders, CheckSquare, ListChecks, ShieldCheck, HardHat,
  UploadSimple, MapPin, Warning, BookOpen, DownloadSimple,
} from '@phosphor-icons/react';

const SECTIONS = [
  { icon: SquaresFour, color: 'text-sky-600', bg: 'bg-sky-50', title: 'Tableau de bord', to: '/dashboard',
    text: "Vue d'ensemble : indicateurs clés, actions en retard, approbations en attente. L'admin national peut filtrer toutes les vues par site via le sélecteur en haut à droite.",
    tips: ['Cliquez une carte pour ouvrir la liste correspondante', 'KPI « Points SiD non conf. » = total des non conformités Safety In Design'] },
  { icon: PlusCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', title: 'Créer une MOC', to: '/moc/new',
    text: "Assistant pas à pas : description du changement, classification (catégorie/nature), puis soumission au circuit d'approbation.",
    tips: ['Les champs Site/Service se limitent à vos sites autorisés'] },
  { icon: Folders, color: 'text-indigo-600', bg: 'bg-indigo-50', title: 'Mes MOC', to: '/mocs',
    text: 'Liste filtrable (recherche, statut, catégorie, nature, site). Un badge rouge « X SiD » signale les points Safety In Design non conformes.',
    tips: ['Cliquez le badge « SiD » pour ouvrir directement les points à traiter'] },
  { icon: ShieldCheck, color: 'text-amber-600', bg: 'bg-amber-50', title: 'Approbations & PSSR', to: '/pssr',
    text: "Circuit d'approbation selon la catégorie, puis Pre-Startup Safety Review avant mise en route.",
    tips: ['Vous pouvez importer un PSSR Word (.docx) pour créer les actions automatiquement'] },
  { icon: HardHat, color: 'text-rose-600', bg: 'bg-rose-50', title: 'Safety In Design', to: null,
    text: "Dans chaque fiche MOC, onglet « Safety In Design » : check-list en phase d'étude. Chaque réponse « Non » permet de créer une action. Bouton « Non conformités » pour n'afficher que les écarts.",
    tips: ['Bouton « Rapport PDF » : export de la check-list + plan d\'actions', 'Les sections sont configurables par l\'admin'] },
  { icon: UploadSimple, color: 'text-cyan-600', bg: 'bg-cyan-50', title: 'Import PSSR (Word)', to: '/pssr',
    text: "Sur la page PSSR, « Importer un PSSR (Word) » lit les tableaux « Actions avant/après mise en route » et crée les actions dans la MOC choisie (ou une nouvelle).",
    tips: ['Vérifiez le responsable et la date avant d\'importer'] },
  { icon: ListChecks, color: 'text-teal-600', bg: 'bg-teal-50', title: 'Actions & Risques', to: '/actions',
    text: 'Suivi des actions correctives, échéances et retards. Matrice des risques dans la fiche MOC.',
    tips: ['Les actions bloquantes en retard déclenchent un rappel e-mail'] },
  { icon: MapPin, color: 'text-violet-600', bg: 'bg-violet-50', title: 'Administration', to: '/admin',
    text: 'Gestion des utilisateurs, rôles et sites (créer/modifier/désactiver), sections Safety In Design, et paramètres des rappels.',
    tips: ['Onglet « Rappels » : délai (J-N), heure d\'envoi et copie au Pilote MOC'] },
];

export default function Aide() {
  const navigate = useNavigate();
  return (
    <div className="space-y-6" data-testid="aide-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Aide visuelle</div>
        <h1 className="font-display text-3xl font-black tracking-tight mt-1">Guide de l'application</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Un aperçu rapide des modules et des nouveautés. Cliquez une carte pour ouvrir le module correspondant.</p>
      </div>

      <Card className="rounded-sm p-5 bg-primary text-primary-foreground flex flex-wrap items-center justify-between gap-4" data-testid="aide-pptx-banner">
        <div className="flex items-start gap-3">
          <BookOpen size={28} weight="bold" className="mt-0.5" />
          <div>
            <div className="font-display font-bold text-lg">Présentation de formation (PowerPoint)</div>
            <div className="text-sm opacity-80">25 diapositives illustrées : parcours MOC, toutes les nouveautés, fiche mémo.</div>
          </div>
        </div>
        <a href="/Formation-MOC.pptx" download data-testid="aide-pptx-download"
          className="inline-flex items-center gap-2 bg-white text-primary font-semibold text-sm px-4 py-2.5 rounded-sm hover:bg-white/90 transition-colors">
          <DownloadSimple size={18} weight="bold" /> Télécharger Formation-MOC.pptx
        </a>
      </Card>

      <MocFlow />
      <GuideViewer />

      <h2 className="font-display font-bold text-lg pt-2">Les modules</h2>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SECTIONS.map((s) => (
          <Card key={s.title} data-testid={`aide-card-${s.title}`}
            onClick={() => s.to && navigate(s.to)}
            className={`rounded-sm p-5 border border-border transition-shadow ${s.to ? 'cursor-pointer hover:shadow-md' : ''}`}>
            <div className={`w-11 h-11 rounded-sm ${s.bg} flex items-center justify-center mb-3`}>
              <s.icon size={24} weight="bold" className={s.color} />
            </div>
            <h3 className="font-display font-bold">{s.title}</h3>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{s.text}</p>
            <ul className="mt-3 space-y-1.5">
              {s.tips.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-foreground/80">
                  <Warning size={14} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>

      <Card className="rounded-sm p-5 bg-muted/40 flex items-start gap-3">
        <BookOpen size={22} weight="bold" className="text-primary mt-0.5" />
        <div className="text-sm">
          <span className="font-semibold">Besoin d'une formation complète ?</span>{' '}
          Téléchargez le support PowerPoint : <a href="/Formation-MOC.pptx" className="text-primary underline" data-testid="aide-pptx-link" download>Formation-MOC.pptx</a>
        </div>
      </Card>
    </div>
  );
}
